package fr.manyverse.app.presentation.components.inventory

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.ExposedDropdownMenuAnchorType
import androidx.compose.material3.ExposedDropdownMenuBox
import androidx.compose.material3.ExposedDropdownMenuDefaults
import androidx.compose.material3.LinearProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.input.KeyboardCapitalization
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.DialogProperties
import fr.manyverse.app.domain.model.ImageData
import fr.manyverse.app.domain.model.ItemCategory
import fr.manyverse.app.domain.util.toDisplayNameFromFileName
import fr.manyverse.app.presentation.components.rememberImagePickerLauncher
import fr.manyverse.app.presentation.components.clearFocusOnTap
import fr.manyverse.app.presentation.components.ImagePickerPreview
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.cancel
import manyverse.shared.generated.resources.confirm
import manyverse.shared.generated.resources.inventory_item_category
import manyverse.shared.generated.resources.inventory_item_image_optional
import manyverse.shared.generated.resources.inventory_item_name
import manyverse.shared.generated.resources.inventory_item_name_hint
import manyverse.shared.generated.resources.inventory_item_quantity
import org.jetbrains.compose.resources.stringResource

/**
 * Popup de saisie/édition d'un objet (nom, catégorie, quantité si consommable, image).
 *
 * Partagée entre le coffre MJ ([fr.manyverse.app.presentation.screens.GmInventoryScreen]) et
 * l'inventaire des pré-tirés. Ne dépend d'aucun ViewModel : elle remonte les valeurs via [onConfirm].
 * [itemKey] réinitialise l'état interne quand l'objet édité change.
 */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun InventoryItemFormDialog(
    title: String,
    itemKey: String?,
    onConfirm: (name: String, category: ItemCategory, quantity: Int, imageData: ImageData?) -> Unit,
    onDismiss: () -> Unit,
    initialName: String = "",
    initialCategory: ItemCategory = ItemCategory.WEAPON,
    initialQuantity: Int = 1,
    isLoading: Boolean = false,
    errorMessage: String? = null,
    currentImageUrl: String? = null
) {
    var name by key(itemKey) { remember { mutableStateOf(initialName) } }
    var selectedCategory by key(itemKey) { remember { mutableStateOf(initialCategory) } }
    var quantity by key(itemKey) { remember { mutableStateOf(initialQuantity.toString()) } }
    var categoryDropdownExpanded by remember { mutableStateOf(false) }
    var selectedImageData by remember { mutableStateOf<ImageData?>(null) }

    val launchImagePicker = rememberImagePickerLauncher { imageData ->
        selectedImageData = imageData
        if (name.isBlank()) name = imageData.fileName.toDisplayNameFromFileName().take(100)
    }

    AlertDialog(
        properties = DialogProperties(dismissOnBackPress = false, dismissOnClickOutside = false),
        modifier = Modifier.clearFocusOnTap(),
        onDismissRequest = { if (!isLoading) onDismiss() },
        title = { Text(title) },
        text = {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .verticalScroll(rememberScrollState()),
                verticalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                OutlinedTextField(
                    value = name,
                    onValueChange = { if (it.length <= 100) name = it },
                    label = { Text(stringResource(Res.string.inventory_item_name)) },
                    placeholder = { Text(stringResource(Res.string.inventory_item_name_hint)) },
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true,
                    enabled = !isLoading,
                    supportingText = { Text("${name.length}/100") },
                    keyboardOptions = KeyboardOptions(capitalization = KeyboardCapitalization.Sentences)
                )

                ExposedDropdownMenuBox(
                    expanded = categoryDropdownExpanded && !isLoading,
                    onExpandedChange = {
                        categoryDropdownExpanded = !categoryDropdownExpanded && !isLoading
                    }
                ) {
                    OutlinedTextField(
                        value = selectedCategory.label(),
                        onValueChange = {},
                        readOnly = true,
                        label = { Text(stringResource(Res.string.inventory_item_category)) },
                        trailingIcon = {
                            ExposedDropdownMenuDefaults.TrailingIcon(expanded = categoryDropdownExpanded)
                        },
                        colors = ExposedDropdownMenuDefaults.outlinedTextFieldColors(),
                        modifier = Modifier
                            .fillMaxWidth()
                            .menuAnchor(ExposedDropdownMenuAnchorType.PrimaryEditable, true),
                        enabled = !isLoading
                    )
                    ExposedDropdownMenu(
                        expanded = categoryDropdownExpanded,
                        onDismissRequest = { categoryDropdownExpanded = false }
                    ) {
                        ItemCategory.entries.forEach { category ->
                            DropdownMenuItem(
                                text = { Text(category.label()) },
                                onClick = {
                                    selectedCategory = category
                                    categoryDropdownExpanded = false
                                }
                            )
                        }
                    }
                }

                if (selectedCategory == ItemCategory.CONSUMABLE) {
                    OutlinedTextField(
                        value = quantity,
                        onValueChange = { value ->
                            if (value.all { it.isDigit() } && value.length <= 3) quantity = value
                        },
                        label = { Text(stringResource(Res.string.inventory_item_quantity)) },
                        modifier = Modifier.fillMaxWidth(),
                        singleLine = true,
                        enabled = !isLoading,
                        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number)
                    )
                }

                ImagePickerPreview(
                    selectedImageData = selectedImageData,
                    onPickImage = { launchImagePicker() },
                    onClearImage = { selectedImageData = null },
                    pickLabel = stringResource(Res.string.inventory_item_image_optional),
                    enabled = !isLoading,
                    currentImageUrl = currentImageUrl
                )

                if (errorMessage != null) {
                    Text(
                        text = errorMessage,
                        color = MaterialTheme.colorScheme.error,
                        style = MaterialTheme.typography.bodySmall
                    )
                }

                if (isLoading) {
                    LinearProgressIndicator(modifier = Modifier.fillMaxWidth())
                }
            }
        },
        confirmButton = {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                TextButton(onClick = onDismiss, enabled = !isLoading) {
                    Text(stringResource(Res.string.cancel))
                }
                Button(
                    onClick = {
                        val qty = if (selectedCategory == ItemCategory.CONSUMABLE) {
                            quantity.toIntOrNull()?.coerceIn(1, 999) ?: 1
                        } else {
                            1
                        }
                        onConfirm(name.trim(), selectedCategory, qty, selectedImageData)
                    },
                    enabled = !isLoading && name.isNotBlank()
                ) {
                    Text(stringResource(Res.string.confirm))
                }
            }
        }
    )
}
